import { useMemo } from "react";
import {
  isQuestionAnswered,
  hasMeaningfulValue,
} from "../utils/optionalDataUtils";

/**
 * Hook to calculate comprehensive compliance metrics for a client report
 * Returns a 1-5 star rating based on exercise, nutrition, daily check-ins, and weekly questions
 */
const useReportCompliance = (report) => {
  const compliance = useMemo(() => {
    if (!report) {
      return {
        overallRating: 0,
        overallScore: 0,
        categories: {},
        summary: "No report data available",
      };
    }

    // Convert Firebase timestamps to dates
    const startDate = new Date(report.startDate.seconds * 1000);
    const endDate = new Date(report.endDate.seconds * 1000);
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

    // Helper function to calculate text response quality (0-1)
    const calculateTextQuality = (text) => {
      if (!text || typeof text !== "string") return 0;
      const trimmed = text.trim();
      if (trimmed.length === 0) return 0;
      if (trimmed.length < 10) return 0.3;
      if (trimmed.length < 30) return 0.6;
      if (trimmed.length < 100) return 0.8;
      return 1.0;
    };

    // 1. Exercise Compliance (25% weight)
    const calculateExerciseCompliance = () => {
      const exerciseData = report.exerciseData || [];

      if (exerciseData.length === 0) {
        return { score: 0, details: "No exercise data logged" };
      }

      let completedWorkouts = 0;
      let totalExercises = 0;
      let completedExercises = 0;
      let totalSets = 0;
      let completedSets = 0;

      exerciseData.forEach((workout) => {
        if (workout.status === "complete") {
          completedWorkouts++;
        }

        if (workout.reportData?.exercises) {
          workout.reportData.exercises.forEach((exercise) => {
            // Exclude skipped exercises from compliance calculations
            if (exercise.skipped === true) {
              return; // Skip this exercise
            }

            totalExercises++;
            if (exercise.completed) {
              completedExercises++;
            }

            if (exercise.sets) {
              exercise.sets.forEach((set) => {
                // Exclude skipped sets from compliance calculations
                if (set.skipped === true) {
                  return; // Skip this set
                }

                totalSets++;
                if (set.completed && set.weight > 0 && set.reps > 0) {
                  completedSets++;
                }
              });
            }
          });
        }
      });

      // Calculate sub-scores
      const workoutCompletionRate =
        exerciseData.length > 0 ? completedWorkouts / exerciseData.length : 0;
      const exerciseCompletionRate =
        totalExercises > 0 ? completedExercises / totalExercises : 0;
      const setQualityRate = totalSets > 0 ? completedSets / totalSets : 0;

      // Weighted exercise score
      const exerciseScore =
        workoutCompletionRate * 0.4 +
        exerciseCompletionRate * 0.3 +
        setQualityRate * 0.3;

      return {
        score: exerciseScore,
        details: {
          workoutsLogged: exerciseData.length,
          workoutsCompleted: completedWorkouts,
          workoutCompletionRate: Math.round(workoutCompletionRate * 100),
          exerciseCompletionRate: Math.round(exerciseCompletionRate * 100),
          setQualityRate: Math.round(setQualityRate * 100),
        },
      };
    };

    // 2. Nutrition Compliance (30% weight)
    const calculateNutritionCompliance = () => {
      const healthData = report.healthData || [];

      if (healthData.length === 0) {
        return { score: 0, details: "No nutrition data logged" };
      }

      let daysWithMacros = 0;
      let daysWithWater = 0;
      let totalGoalAdherence = 0;
      let goalAdherenceDays = 0;

      healthData.forEach((day) => {
        const data = day.reportData;
        const clientData = day.clientData;
        if (!data || !clientData) return;

        // Check for meaningful macro logging based on daily goals
        const hasGoals =
          clientData.proteins > 0 ||
          clientData.carbs > 0 ||
          clientData.fats > 0;
        const hasMeaningfulCalories = data.kCal > 100; // Basic threshold for any logging
        const hasMeaningfulMacros =
          data.proteins > 0 || data.carbs > 0 || data.fats > 0;

        if (hasMeaningfulCalories && hasMeaningfulMacros) {
          daysWithMacros++;

          // Calculate goal adherence if goals are set
          if (hasGoals) {
            let dayAdherence = 0;
            let macroCount = 0;

            // Calculate adherence for each macro (optimal range: 80-120% of goal)
            if (clientData.proteins > 0) {
              const proteinAdherence = Math.min(
                data.proteins / clientData.proteins,
                1.5,
              ); // Cap at 150%
              const proteinScore =
                proteinAdherence >= 0.8 && proteinAdherence <= 1.2
                  ? 1.0
                  : proteinAdherence >= 0.6 && proteinAdherence <= 1.4
                    ? 0.8
                    : proteinAdherence >= 0.4 && proteinAdherence <= 1.6
                      ? 0.6
                      : proteinAdherence > 0
                        ? 0.4
                        : 0;
              dayAdherence += proteinScore;
              macroCount++;
            }

            if (clientData.carbs > 0) {
              const carbAdherence = Math.min(
                data.carbs / clientData.carbs,
                1.5,
              );
              const carbScore =
                carbAdherence >= 0.8 && carbAdherence <= 1.2
                  ? 1.0
                  : carbAdherence >= 0.6 && carbAdherence <= 1.4
                    ? 0.8
                    : carbAdherence >= 0.4 && carbAdherence <= 1.6
                      ? 0.6
                      : carbAdherence > 0
                        ? 0.4
                        : 0;
              dayAdherence += carbScore;
              macroCount++;
            }

            if (clientData.fats > 0) {
              const fatAdherence = Math.min(data.fats / clientData.fats, 1.5);
              const fatScore =
                fatAdherence >= 0.8 && fatAdherence <= 1.2
                  ? 1.0
                  : fatAdherence >= 0.6 && fatAdherence <= 1.4
                    ? 0.8
                    : fatAdherence >= 0.4 && fatAdherence <= 1.6
                      ? 0.6
                      : fatAdherence > 0
                        ? 0.4
                        : 0;
              dayAdherence += fatScore;
              macroCount++;
            }

            if (macroCount > 0) {
              totalGoalAdherence += dayAdherence / macroCount;
              goalAdherenceDays++;
            }
          }
        }

        if (data.water > 0) {
          daysWithWater++;
        }
      });

      const macroTrackingRate =
        healthData.length > 0 ? daysWithMacros / healthData.length : 0;
      const waterTrackingRate =
        healthData.length > 0 ? daysWithWater / healthData.length : 0;
      const consistencyRate = totalDays > 0 ? healthData.length / totalDays : 0;
      const goalAdherenceRate =
        goalAdherenceDays > 0 ? totalGoalAdherence / goalAdherenceDays : 0;

      // Weighted nutrition score with goal adherence as primary factor
      const nutritionScore =
        goalAdherenceRate * 0.4 + // Goal adherence is most important
        macroTrackingRate * 0.3 + // Tracking consistency
        waterTrackingRate * 0.15 + // Water tracking
        consistencyRate * 0.15; // Overall consistency

      return {
        score: nutritionScore,
        details: {
          daysLogged: healthData.length,
          daysWithMacros,
          daysWithWater,
          goalAdherenceDays,
          macroTrackingRate: Math.round(macroTrackingRate * 100),
          waterTrackingRate: Math.round(waterTrackingRate * 100),
          consistencyRate: Math.round(consistencyRate * 100),
          goalAdherenceRate: Math.round(goalAdherenceRate * 100),
        },
      };
    };

    // 3. Daily Check-in Compliance (20% weight)
    const calculateDailyCheckInCompliance = () => {
      const healthData = report.healthData || [];

      if (healthData.length === 0) {
        return { score: 0, details: "No daily check-in data" };
      }

      let totalOptionalFields = 0;
      let completedOptionalFields = 0;
      let qualityScore = 0;

      healthData.forEach((day) => {
        const optionalData = day.reportData?.optional || {};

        Object.values(optionalData).forEach((field) => {
          totalOptionalFields++;
          // Use isQuestionAnswered to check if the question was explicitly answered
          if (isQuestionAnswered(field)) {
            completedOptionalFields++;
            // Use hasMeaningfulValue to assess the quality of the answer
            if (hasMeaningfulValue(field)) {
              if (typeof field.value === "string") {
                qualityScore += calculateTextQuality(field.value);
              } else {
                qualityScore += 0.8; // Numeric values get good score if meaningful
              }
            } else {
              // Question was answered but with minimal content (e.g., "No" answer)
              qualityScore += 0.5;
            }
          }
        });
      });

      const completionRate =
        totalOptionalFields > 0
          ? completedOptionalFields / totalOptionalFields
          : 0;
      const avgQuality =
        completedOptionalFields > 0
          ? qualityScore / completedOptionalFields
          : 0;

      const dailyCheckInScore = completionRate * 0.7 + avgQuality * 0.3;

      return {
        score: dailyCheckInScore,
        details: {
          totalFields: totalOptionalFields,
          completedFields: completedOptionalFields,
          completionRate: Math.round(completionRate * 100),
          averageQuality: Math.round(avgQuality * 100),
        },
      };
    };

    // 4. Weekly Questions Compliance (20% weight)
    const calculateWeeklyQuestionsCompliance = () => {
      const weeklyOptionals = report.weeklyOptionals || [];
      const extraData = report.extraData?.optionals || {};

      if (weeklyOptionals.length === 0) {
        return { score: 0, details: "No weekly questions configured" };
      }

      let requiredQuestions = 0;
      let answeredQuestions = 0;
      let qualityScore = 0;

      weeklyOptionals.forEach((question) => {
        if (question.isRequired) {
          requiredQuestions++;

          const answer = extraData[question.id];
          // Use isQuestionAnswered to check if the question was explicitly answered
          if (answer && isQuestionAnswered(answer)) {
            answeredQuestions++;
            // Use hasMeaningfulValue to assess the quality of the answer
            if (hasMeaningfulValue(answer)) {
              if (typeof answer.value === "string") {
                qualityScore += calculateTextQuality(answer.value);
              } else {
                qualityScore += 0.8;
              }
            } else {
              // Question was answered but with minimal content
              qualityScore += 0.5;
            }
          }
        }
      });

      const completionRate =
        requiredQuestions > 0 ? answeredQuestions / requiredQuestions : 0;
      const avgQuality =
        answeredQuestions > 0 ? qualityScore / answeredQuestions : 0;

      const weeklyQuestionsScore = completionRate * 0.8 + avgQuality * 0.2;

      return {
        score: weeklyQuestionsScore,
        details: {
          totalRequired: requiredQuestions,
          answered: answeredQuestions,
          completionRate: Math.round(completionRate * 100),
          averageQuality: Math.round(avgQuality * 100),
        },
      };
    };

    // 5. Supplement Compliance (5% weight)
    const calculateSupplementCompliance = () => {
      const supplementData = report.supplementData || [];
      const prescriptions = report.clientData?.supplementPrescriptions || [];

      if (prescriptions.length === 0) {
        return {
          score: 1,
          details: {
            status: "No supplements prescribed",
            prescribed: 0,
            logged: supplementData.length,
            trackingDays: 0,
            hasLogging: supplementData.length > 0,
          },
        };
      }

      if (supplementData.length === 0) {
        return {
          score: 0,
          details: {
            status: "Supplements prescribed but not tracked",
            prescribed: prescriptions.length,
            logged: 0,
            trackingDays: 0,
            hasLogging: false,
          },
        };
      }

      // Count days with supplement tracking
      const trackingDays = supplementData.length;
      const trackingRate = trackingDays / totalDays;

      // Score based on tracking consistency
      const supplementScore = Math.min(trackingRate * 1.2, 1.0); // Give some bonus for consistency

      return {
        score: supplementScore,
        details: {
          status:
            trackingDays > 0
              ? "Actively tracking supplements"
              : "No tracking logged",
          prescribed: prescriptions.length,
          logged: supplementData.length,
          trackingDays: trackingDays,
          trackingRate: Math.round(trackingRate * 100),
          hasLogging: supplementData.length > 0,
        },
      };
    };

    // Calculate all category scores
    const exercise = calculateExerciseCompliance();
    const nutrition = calculateNutritionCompliance();
    const dailyCheckIn = calculateDailyCheckInCompliance();
    const weeklyQuestions = calculateWeeklyQuestionsCompliance();
    const supplements = calculateSupplementCompliance();

    // Determine which categories have actual tracking data (not just zero scores)
    const isTrackingCategory = {
      exercise: (report.exerciseData || []).length > 0,
      nutrition: (report.healthData || []).length > 0,
      dailyCheckIn: (report.healthData || []).some((day) => {
        const optionalData = day.reportData?.optional || {};
        return Object.keys(optionalData).length > 0;
      }),
      weeklyQuestions: (report.weeklyOptionals || []).length > 0,
      supplements: (report.supplementData || []).length > 0,
    };

    // Calculate weights for categories that are actually being tracked
    const categoryWeights = {
      exercise: 0.25,
      nutrition: 0.3,
      dailyCheckIn: 0.2,
      weeklyQuestions: 0.2,
      supplements: 0.05,
    };

    // Calculate the total weight of categories being tracked
    let totalTrackingWeight = 0;
    let weightedScore = 0;

    if (isTrackingCategory.exercise) {
      totalTrackingWeight += categoryWeights.exercise;
      weightedScore += exercise.score * categoryWeights.exercise;
    }
    if (isTrackingCategory.nutrition) {
      totalTrackingWeight += categoryWeights.nutrition;
      weightedScore += nutrition.score * categoryWeights.nutrition;
    }
    if (isTrackingCategory.dailyCheckIn) {
      totalTrackingWeight += categoryWeights.dailyCheckIn;
      weightedScore += dailyCheckIn.score * categoryWeights.dailyCheckIn;
    }
    if (isTrackingCategory.weeklyQuestions) {
      totalTrackingWeight += categoryWeights.weeklyQuestions;
      weightedScore += weeklyQuestions.score * categoryWeights.weeklyQuestions;
    }
    if (isTrackingCategory.supplements) {
      totalTrackingWeight += categoryWeights.supplements;
      weightedScore += supplements.score * categoryWeights.supplements;
    }

    // Calculate overall score based only on tracked categories
    const overallScore =
      totalTrackingWeight > 0 ? weightedScore / totalTrackingWeight : 0;

    // Convert to 1-5 star rating
    let overallRating;
    if (overallScore >= 0.9) overallRating = 5;
    else if (overallScore >= 0.8) overallRating = 4;
    else if (overallScore >= 0.7) overallRating = 3;
    else if (overallScore >= 0.6) overallRating = 2;
    else overallRating = 1;

    // Generate summary
    const generateSummary = () => {
      const issues = [];
      // Only check categories that are actually being tracked
      if (isTrackingCategory.exercise && exercise.score < 0.7)
        issues.push("exercise tracking");
      if (isTrackingCategory.nutrition && nutrition.score < 0.7)
        issues.push("nutrition logging");
      if (isTrackingCategory.dailyCheckIn && dailyCheckIn.score < 0.7)
        issues.push("daily check-ins");
      if (isTrackingCategory.weeklyQuestions && weeklyQuestions.score < 0.7)
        issues.push("weekly questions");

      if (issues.length === 0) {
        return "Excellent compliance across all areas";
      } else if (issues.length <= 2) {
        return `Good overall compliance, could improve: ${issues.join(", ")}`;
      } else {
        return `Multiple areas need attention: ${issues.join(", ")}`;
      }
    };

    return {
      overallRating,
      overallScore: Math.round(overallScore * 100),
      categories: {
        exercise,
        nutrition,
        dailyCheckIn,
        weeklyQuestions,
        supplements,
      },
      tracking: isTrackingCategory,
      excludedCategories: Object.keys(isTrackingCategory).filter(
        (key) => !isTrackingCategory[key],
      ),
      trackedCategories: Object.keys(isTrackingCategory).filter(
        (key) => isTrackingCategory[key],
      ),
      summary: generateSummary(),
      reportPeriod: {
        startDate,
        endDate,
        totalDays,
      },
    };
  }, [report]);

  return compliance;
};

export default useReportCompliance;
